<template>
  <div
    class="flex h-full bg-gray-50 dark:bg-surface-900"
    :class="{ 'overflow-hidden': isOpen }"
  >
    <Sidebar
      ref="sidebarRef"
      v-on-click-outside="shouldCloseSidebar"
      :menu-items="menuItems"
    />
    <div class="flex overflow-x-hidden flex-col flex-1 w-full">
      <NavigationMenu
        ref="navigationMenuRef"
        v-bind="navigationMenuProps"
      />
      <main class="overflow-y-auto h-full bg-gray-100 dark:bg-surface-900">
        <slot name="header" />
        <div class="container max-w-full min-h-screen">
          <slot />
        </div>
        <Footer />
      </main>
    </div>
  </div>
</template>

<script setup>
import Sidebar from "~/components/sidebar.vue";
import { useSidebarStore } from "~/stores/sidebar";
import { vOnClickOutside } from "@vueuse/components";
import NavigationMenu from "~/components/navigationMenu.vue";
import Permissions from "~/types/Permissions";
import PendingPermissions from "~/types/PendingPermissions";
import Role from "~/types/Role";
import UserFirstTimeSecurityQuestionsSetup from "~/components/user/FirstTimeSecurityQuestionsSetup.vue";

const sidebarStore = useSidebarStore();
const securityQuestionsStore = useSecurityQuestionsStore();
const dialog = useDialog();
const { checkAndShowSetupIfNeeded } = useSecurityQuestions();
const sidebarRef = ref(null);
const navigationMenuRef = ref(null);
const userStore = useUserStore();
const user = await userStore.getUser();
const { isOpen } = storeToRefs(sidebarStore);
const { closeSidebar } = sidebarStore;
const navigationMenuProps = {
  HasToggleableMenu: true,
};
const menuItems = [
  {
    key: "0",
    label: "Tableau de bord",
    href: "/",

    icon: "dasboard-icon",
    param: "",
  },
  {
    key: "1",
    label: "Villes",
    href: "/cities",
    permissions: [Permissions.CitiesList, Permissions.CitiesView],
    icon: "building-library",
    new: true,
  },
  {
    key: "2",
    label: "Marchés",
    href: "/markets",
    permissions: [Permissions.MarketsList],
    icon: "location-icon",
    new: true,
  },
  {
    key: "3",
    label: "Placiers",
    permissions: [
      Permissions.PlacersList,
      PendingPermissions.ReadPlacerSchedule,
      PendingPermissions.ReadOwnPlacerSchedule,
    ],
    items: [
      {
        key: "3_1",
        label: "Placiers",
        permissions: [Permissions.PlacersList],
        href: "/placers",
      },
      {
        key: "3_2",
        label: "Appareils",
        permissions: [Permissions.MaterialsList, Permissions.PlacersList],
        href: "/materials",
        new: true,
      },
      {
        key: "3_3",
        label: "Planning",
        permissions: [
          PendingPermissions.ReadPlacerSchedule,
          PendingPermissions.ReadOwnPlacerSchedule,
        ],
        href: "/placer-planning",
        new: true,
      },
    ],
    icon: "team-icon",
    new: true,
  },
  {
    key: "4",
    label: "Commerçants",
    // Union des permissions des enfants (nouvelles + legacy).
    permissions: [
      Permissions.CompaniesList,
      Permissions.MerchantsList,
      Permissions.SubscriptionsList,
      Permissions.SubscriptionOffersList,
      Permissions.LocationsList,
      Permissions.RenewalsAccess,
      Permissions.RenewalExceptionsList,
      Permissions.DeduplicationMerchantsList,
      Permissions.DeduplicationCompaniesList,
      Permissions.DocumentRequirementsList,
      Permissions.MerchantsList,
      Permissions.SubscriptionsList,
      Permissions.RenewalsAccess,
    ],
    items: [
      {
        key: "4_1",
        label: "Entreprises",
        href: "/companies",
        permissions: [Permissions.CompaniesList],
      },
      {
        key: "4_2",
        label: "Commerçants",
        permissions: [Permissions.MerchantsList],
        href: "/merchants",
      },
      {
        key: "4_3",
        label: "Abonnements",
        href: "/subscriptions",
        permissions: [Permissions.SubscriptionsList],
        new: true,
      },
      {
        key: "4_4",
        label: "Offres d'abonnement",
        href: "/invoices/offer-invoices",
        permissions: [Permissions.SubscriptionOffersList, Permissions.SubscriptionsList],
        new: true,
      },
      {
        key: "4_5",
        label: "Emplacements",
        href: "/locations",
        permissions: [Permissions.LocationsList, Permissions.SubscriptionsList],
        new: true,
      },
      {
        key: "4_6",
        label: "Reconductions",
        permissions: [Permissions.RenewalsAccess],
        href: "/renewals",
        new: true,
      },
      {
        key: "4_7",
        label: "Exceptions de reconduction",
        href: "/subscriptions/renewal_exceptions",
        permissions: [Permissions.RenewalExceptionsList],
      },
      {
        key: "4_8",
        label: "Gérer les doublons",
        items: [
          {
            key: "4_8_1",
            label: "Commerçants",
            href: "/deduplications",
            permissions: [Permissions.DeduplicationMerchantsList],
          },
          {
            key: "4_8_2",
            label: "Entreprises",
            href: "/deduplications/companies",
            permissions: [Permissions.DeduplicationCompaniesList],
          },
        ],
      },
      {
        key: "4_9",
        label: "Pièces à fournir",
        href: "/document-required",
        permissions: [Permissions.DocumentRequirementsList],
      },
      {
        key: "4_10",
        label: "Audit passagers",
        href: "/audit-passengers/backoffice",
        roles: [
          Role.ADMIN,
          Role.SUPER_ADMIN,
          Role.HEAD_OF_OPERATION,
          Role.HEAD_OF_PLACERS,
          Role.MANAGER,
        ],
        permissions: [Permissions.MerchantsList],
        new: true,
      },
    ],
    icon: "merchant",
    new: true,
  },
  {
    key: "5",
    label: "Facturation",
    icon: "document-icon",
    // Union des permissions des enfants (nouvelles + legacy).
    permissions: [
      Permissions.ProductsList,
      Permissions.InvoicesList,
      Permissions.InvoicesEmailFailuresList,
      Permissions.BillingEntitiesList,
      Permissions.PaymentsList,
      Permissions.ProductsList,
      Permissions.InvoicesList,
      Permissions.PaymentsList,
      Permissions.InvoicesList,
      PendingPermissions.ViewPaymentRateReporting,
    ],
    items: [
      {
        key: "5_1",
        label: "Articles",
        permissions: [Permissions.ProductsList],
        href: "/products",
        new: true,
      },
      {
        key: "5_2",
        label: "Factures",
        items: [
          {
            key: "5_2_1",
            label: "Émises",
            permissions: [Permissions.InvoicesList],
            href: "/invoices",
            new: true,
          },
          {
            key: "5_2_2",
            label: "Brouillons et archives",
            permissions: [Permissions.InvoicesList],
            href: "/invoices/noactive-invoices",
            new: true,
          },
          {
            key: "5_2_3",
            label: "Avoirs",
            permissions: [Permissions.InvoicesList],
            href: "/invoices/credit-invoices",
            new: true,
          },
        ],
        permissions: [Permissions.InvoicesList],
        new: true,
      },
      {
        key: "5_2_3",
        label: "Échecs d'envoi d'emails",
        permissions: [Permissions.InvoicesEmailFailuresList, Permissions.InvoicesList],
        href: "/email-failures",
        new: true,
      },
      {
        key: "5_3",
        label: "Entités",
        href: "/billing-entities",
        permissions: [Permissions.BillingEntitiesList],
        new: true,
      },
      {
        key: "5_4",
        label: "Paiements",
        permissions: [Permissions.PaymentsList],
        href: "/payments",
        new: true,
      },
      {
        key: "5_5",
        label: "Suivi des encaissements",
        permissions: [PendingPermissions.ViewPaymentRateReporting],
        href: "/reporting/payment-rates",
        new: true,
      },
    ],
    new: true,
  },
  {
    key: "6",
    label: "Comptabilité",
    icon: "money-icon",
    // Union des permissions des enfants (nouvelles + legacy) : le parent est visible
    // dès qu'un enfant l'est.
    permissions: [
      Permissions.DepositsList,
      Permissions.CashregistersList,
      Permissions.BalancesList,
      Permissions.AccountingExportsGenerate,
      Permissions.DepositsList,
      Permissions.CashregistersList,
      Permissions.BalancesList,
      Permissions.AccountingExportsGenerate,
    ],
    items: [
      {
        key: "6_1",
        label: "Dépôts",
        href: "/deposits",
        permissions: [Permissions.DepositsList],
      },
      {
        key: "6_2",
        label: "Caisses",
        href: "/cashregisters",
        permissions: [Permissions.CashregistersList],
      },
      {
        key: "6_3",
        label: "Balances",
        href: "/balances",
        permissions: [Permissions.BalancesList],
      },
      {
        key: "6_4",
        label: "Export comptables",
        permissions: [Permissions.AccountingExportsGenerate],
        action: function () {
          console.log("export comptable");
        },
      },
    ],
  },
  {
    key: "equipments",
    label: "Équipements",
    href: "/equipments",
    permissions: [Permissions.EquipmentsList],
    icon: "wrench",
    new: true,
  },
  {
    key: "7",
    label: "Signalements",
    href: "/events",
    icon: "exclamation-triangle",
    permissions: [Permissions.EventsList],
  },
  {
    key: "8",
    label: "Absences",
    href: "/absences",
    // Pilote refonte permissions (lot 3) : visibilité déduite de la permission de la
    // page cible, avec tolérance transitoire sur l'ancienne (retirée au nettoyage).
    permissions: [Permissions.AbsencesList],
    icon: "calendar-minus",
  },
  {
    key: "9",
    icon: "user-icon",
    label: "Utilisateurs",
    permissions: [Permissions.UsersList],
    href: "/users",
  },
  {
    key: "10",
    href: "/settings",
    permissions: [Permissions.SettingsAccess],
    icon: "cog-icon",
    label: "Paramètres",
  },
];
function shouldCloseSidebar(event) {
  if (
    event.target !== navigationMenuRef?.value
    && (
      !navigationMenuRef?.value?.hamburgerMenuRef?.contains(event.target)
    )
  ) {
    closeSidebar();
  }
}
const { setupNotificationSources, setupEventHandlers } = usePdfNotifications();

// Security Questions Dialog
function showSecurityQuestionsDialog() {
  dialog.open(UserFirstTimeSecurityQuestionsSetup, {
    props: {
      style: {
        width: "50rem",
      },
      breakpoints: {
        "960px": "75vw",
        "640px": "90vw",
      },
      modal: true,
      dismissableMask: false,
      closable: true,
    },
    onClose: (options) => {
      const data = options?.data;
      if (data?.action === "completed") {
        securityQuestionsStore.completeSetup();
      }
      securityQuestionsStore.skipSetup();
    },
  });
}

// Watch for security questions setup requirement
const { showSetupModal } = storeToRefs(securityQuestionsStore);
watch(
  showSetupModal,
  (shouldShow) => {
    if (shouldShow) {
      showSecurityQuestionsDialog();
    }
  },
  { immediate: true },
);

onMounted(async () => {
  const { pdfGenerationSource } = await setupNotificationSources(toValue(user));

  if (pdfGenerationSource) {
    setupEventHandlers(pdfGenerationSource);
  }

  // Check and show security questions setup if needed
  await checkAndShowSetupIfNeeded();
});
</script>
